import { progress, router } from "@inertiajs/vue3";
import { onBeforeUnmount, onMounted } from "vue";
import { toast } from "vue-sonner";

import { organizationDiscussionThreadsPath } from "@/routes";

interface UseDiscussionThreadRecoveryOptions {
  getOrganizationId?: () => number | undefined;
}

const discussionMessageRequestPathRegex =
  /^\/organizations\/\d+\/discussion_threads\/\d+\/discussion_messages$/;

const discussionThreadsSyncProps = [
  "threads_active",
  "threads_archived",
  "selected_thread",
  "messages",
  "attachments",
  "mentionables",
] as const;

export function useDiscussionThreadRecovery(
  options: UseDiscussionThreadRecoveryOptions = {},
) {
  const removeInvalidHook = router.on("invalid", (event) => {
    const response = (event as unknown as {
      detail?: {
        response?: {
          status?: number;
          config?: { method?: string; url?: string };
        };
      };
    }).detail?.response;

    if (!response) return;
    if (response.status !== 403 && response.status !== 404) return;
    if (response.config?.method?.toLowerCase() !== "post") return;
    if (!response.config?.url) return;

    let requestPath = "";
    try {
      requestPath = new URL(response.config.url, window.location.origin).pathname;
    } catch {
      return;
    }
    if (!discussionMessageRequestPathRegex.test(requestPath)) return;

    event.preventDefault();
    progress.remove();
    toast.warning("Ya no tienes acceso a este hilo.");

    const organizationId = options.getOrganizationId?.();
    if (organizationId) {
      router.visit(organizationDiscussionThreadsPath(organizationId), {
        replace: true,
        preserveScroll: true,
        preserveState: false,
      });
    }
    return false;
  });

  const handleServiceWorkerMessage = (event: MessageEvent) => {
    if (event.data?.type === "pwa.sync.completed") {
      router.reload({ only: [...discussionThreadsSyncProps], preserveScroll: true });
      return;
    }

    if (event.data?.type === "pwa.sync.failed") {
      toast.error("Error al sincronizar mensajes");
    }
  };

  onMounted(() => {
    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);
  });

  onBeforeUnmount(() => {
    navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    removeInvalidHook();
  });
}
